import { ControlledInput } from '@/components/forms/controlled-input';
import { ControlledDataTableSelect } from '@/components/forms/controlled-datatable-select'; // Updated import
import { ControlledSelect } from '@/components/forms/controlled-select';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Pagination } from '@/components/ui/pagination';
import { getApiErrorMessage } from '@/utils/api-error';
import { showPlanLimit } from '@/store/planLimitStore';
import { formatCurrency } from '@/utils/format-currency';
import api from '@/services/api';
import { showToast } from '@/store/toastStore';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Pressable, ScrollView, View } from 'react-native';
import { PageLayout } from '@/components/ui/page-layout';
import { DataTable } from 'react-native-paper';
import { useThemedHex } from '@/hooks/use-theme-colors';

type ClientData = {
  id: string;
  user?: { username?: string; email?: string };
  phone?: string;
  enrollments?: any[];
};

type FormValues = {
  clientMode: 'existing' | 'new';
  clientSearch: string;
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  clientAddress?: string;

  serviceId?: string;
  price?: string;
  startDate?: string; // DD/MM/AAAA
  continueCharging: boolean;
  endDate?: string; // DD/MM/AAAA

  // Dados de agendamento do serviço
  serviceSchedule?: {
    frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
    daysOfWeek?: string[]; // Para WEEKLY
    dayOfMonth?: string; // Para MONTHLY
    startTime?: string; // HH:mm
    endTime?: string; // HH:mm
  };

  // Dados de configuração de cobrança
  chargeSchedule?: {
    billingModel?: 'RECURRING' | 'ONE_TIME';
    recurrenceInterval?: 'WEEKLY' | 'MONTHLY';
    chargeDay?: string;
    dueDate?: string; // Para ONE_TIME
    reminderType?:
      | 'ONLY_ON_DUE_DATE'
      | 'THREE_DAYS_BEFORE_AND_ON_DUE'
      | 'FIVE_DAYS_BEFORE_AND_ON_DUE'
      | 'CUSTOM_DAYS_BEFORE_AND_ON_DUE';
    customReminderDays?: string;
  };

  // Dados de pagamento
  pixCopyPaste?: string;
  paymentLink?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  paymentInstructions?: string;

  months: {
    competence: string; // YYYY-MM
    received: boolean;
    amount?: string;
    paidAt?: string; // DD/MM/AAAA
  }[];
};

const STEPS = [
  { id: 1, title: 'Cliente', icon: 'person-outline' },
  { id: 2, title: 'Contrato', icon: 'document-text-outline' },
  { id: 3, title: 'Meses', icon: 'calendar-outline' },
  { id: 4, title: 'Revisão', icon: 'checkmark-circle-outline' },
] as const;

const frequencyOptions = [
  { label: 'Diário', value: 'WEEKLY' },
  { label: 'Mensal', value: 'MONTHLY' },
];

const dayOfWeekOptions = [
  { label: 'Domingo', value: '0' },
  { label: 'Segunda', value: '1' },
  { label: 'Terça', value: '2' },
  { label: 'Quarta', value: '3' },
  { label: 'Quinta', value: '4' },
  { label: 'Sexta', value: '5' },
  { label: 'Sábado', value: '6' },
];

const billingModelOptions = [
  { label: 'Recorrente', value: 'RECURRING' },
  { label: 'Única', value: 'ONE_TIME' },
];

const recurrenceIntervalOptions = [
  { label: 'Semanal', value: 'WEEKLY' },
  { label: 'Mensal', value: 'MONTHLY' },
];

const reminderTypeOptions = [
  { label: 'Apenas no dia de vencimento', value: 'ONLY_ON_DUE_DATE' },
  { label: '3 dias antes + no dia', value: 'THREE_DAYS_BEFORE_AND_ON_DUE' },
  { label: '5 dias antes + no dia', value: 'FIVE_DAYS_BEFORE_AND_ON_DUE' },
];

// Limites de data para cadastro retroativo
const MIN_YEAR = new Date().getFullYear() - 50; // 5 anos atrás
const MAX_YEAR = new Date().getFullYear() + 1; // 1 ano no futuro

function validateDateYear(year: number): boolean {
  return year >= MIN_YEAR && year <= MAX_YEAR;
}

function brDateToIso(v?: string, allowFuture = false): string | undefined {
  if (!v) return undefined;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v.trim());
  if (!m) return undefined;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (!dd || !mm || !yyyy) return undefined;

  // Validar ano dentro dos limites
  if (!validateDateYear(yyyy)) {
    return undefined;
  }

  const d = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd)
    return undefined;

  // Se não permitir futuro, validar que a data não seja no futuro
  if (!allowFuture) {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Fim do dia de hoje
    if (d > today) {
      return undefined;
    }
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${yyyy}-${pad(mm)}-${pad(dd)}`;
}

function brDateToYearMonth(v?: string) {
  const iso = brDateToIso(v);
  if (!iso) return undefined;
  return iso.slice(0, 7); // YYYY-MM
}

function moneyToNumber(v?: string) {
  if (!v) return undefined;
  const str = String(v).trim();
  if (str === '' || str === 'R$' || str === 'R$ 0,00') return undefined;

  // Remove "R$", espaços, pontos (separadores de milhar) e substitui vírgula por ponto
  const cleaned = str
    .replace(/R\$\s*/g, '')
    .replace(/\s/g, '')
    .replace(/\./g, '') // Remove todos os pontos (separadores de milhar)
    .replace(',', '.'); // Substitui vírgula por ponto (separador decimal)

  if (cleaned === '' || cleaned === '0' || cleaned === '0.') return undefined;

  const n = Number(cleaned);
  if (Number.isNaN(n)) return undefined;
  return n;
}

function isValidUUID(str?: string): boolean {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

function monthOptions(lastN = 24): { label: string; value: string }[] {
  const now = new Date();
  const res: { label: string; value: string }[] = [];
  // Build ascending (oldest -> newest) to match "Mês 1 = mais longe"
  // Limitar aos últimos 60 meses (5 anos) para evitar opções infinitas
  const maxMonths = 60;
  const actualLastN = Math.min(lastN, maxMonths);

  for (let i = actualLastN - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const yyyy = d.getUTCFullYear();
    // Filtrar apenas meses dentro do limite de anos
    if (yyyy >= MIN_YEAR && yyyy <= MAX_YEAR) {
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      res.push({ value: `${yyyy}-${mm}`, label: `${mm}/${yyyy}` });
    }
  }
  return res;
}

export default function ProviderRetroactiveScreen() {
  const th = useThemedHex();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Manual services fetching state
  const [clients, setClients] = useState<ClientData[]>([]);
  const [isSearchingClients, setIsSearchingClients] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const monthOpts = useMemo(() => monthOptions(36), []);

  const { control, watch, getValues, setValue } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      clientMode: 'existing',
      continueCharging: true,
      months: [{ competence: monthOpts[0]?.value || '', received: true }],
      // Novos defaults
      serviceSchedule: {
        frequency: 'WEEKLY',
      },
      chargeSchedule: {
        billingModel: 'RECURRING',
        recurrenceInterval: 'MONTHLY',
        reminderType: 'THREE_DAYS_BEFORE_AND_ON_DUE',
      },
    },
  });

  // Clear serviceId if it's not a valid UUID (e.g., if user somehow selected "new")
  const serviceIdValue = watch('serviceId');
  useEffect(() => {
    if (serviceIdValue && !isValidUUID(serviceIdValue)) {
      setValue('serviceId', undefined, { shouldDirty: true });
    }
  }, [serviceIdValue, setValue]);

  const { fields, update, move, replace } = useFieldArray({
    control,
    name: 'months',
  });

  const progress = (currentStep / STEPS.length) * 100;
  const clientMode = watch('clientMode');
  const clientSearch = watch('clientSearch');
  const continueCharging = watch('continueCharging');
  const watchedMonths = watch('months');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Watchers para campos condicionais de agendamento e cobrança
  const serviceFrequency = watch('serviceSchedule.frequency');
  const billingModel = watch('chargeSchedule.billingModel');
  const recurrenceInterval = watch('chargeSchedule.recurrenceInterval');
  const reminderType = watch('chargeSchedule.reminderType');

  // Função para gerar meses automaticamente baseado no período
  const generateMonthsFromPeriod = useMemo(() => {
    const startYm = brDateToYearMonth(startDate);
    if (!startYm) return [];

    const endYm = continueCharging
      ? (() => {
          // Se continua cobrando, vai até o mês anterior ao atual (retroativo não inclui mês atual)
          const now = new Date();
          const lastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
          const yyyy = lastMonth.getUTCFullYear();
          const mm = String(lastMonth.getUTCMonth() + 1).padStart(2, '0');
          return `${yyyy}-${mm}`;
        })()
      : brDateToYearMonth(endDate);

    if (!endYm) return [];

    const months: string[] = [];
    const [startYear, startMonth] = startYm.split('-').map(Number);
    const [endYear, endMonth] = endYm.split('-').map(Number);

    let currentYear = startYear;
    let currentMonth = startMonth;

    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
      const yyyy = String(currentYear);
      const mm = String(currentMonth).padStart(2, '0');
      months.push(`${yyyy}-${mm}`);

      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return months;
  }, [startDate, endDate, continueCharging]);

  // Preencher meses automaticamente quando avançar para step 3
  useEffect(() => {
    if (currentStep === 3 && generateMonthsFromPeriod.length > 0) {
      const v = getValues();
      // Só preenche se ainda não tem meses ou se a quantidade mudou
      const currentMonthsCount = v.months?.length || 0;
      if (currentMonthsCount !== generateMonthsFromPeriod.length) {
        const monthsData = generateMonthsFromPeriod.map((competence) => ({
          competence,
          received: false,
          amount: '',
          paidAt: '',
        }));
        replace(monthsData);
      }
    }
  }, [currentStep, generateMonthsFromPeriod, replace, getValues]);

  const filteredMonthOpts = useMemo(() => {
    const startYm = brDateToYearMonth(startDate);
    const endYm = continueCharging
      ? monthOpts[monthOpts.length - 1]?.value
      : brDateToYearMonth(endDate);
    // Fallback: if no bounds, show full list
    if (!startYm && !endYm) return monthOpts;

    return monthOpts.filter((o) => {
      if (startYm && o.value < startYm) return false;
      if (endYm && o.value > endYm) return false;
      return true;
    });
  }, [monthOpts, startDate, endDate, continueCharging]);

  // Paginação dos clientes
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return clients.slice(startIndex, endIndex);
  }, [clients, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(clients.length / itemsPerPage));
  }, [clients.length, itemsPerPage]);

  // Keep months ordered ASC by competence (oldest -> newest),
  // so "Mês 1" is always the farthest month.
  useEffect(() => {
    if (!watchedMonths?.length) return;
    const indexed = watchedMonths
      .map((m, idx) => ({ idx, competence: m?.competence || '' }))
      .filter((x) => x.competence);
    const sorted = [...indexed].sort((a, b) => a.competence.localeCompare(b.competence));
    const isSameOrder =
      indexed.length === sorted.length && indexed.every((x, i) => x.idx === sorted[i]?.idx);
    if (isSameOrder) return;

    // Reorder using move() to preserve RHF field array ids
    const currentOrder = indexed.map((x) => x.idx);
    const desiredOrder = sorted.map((x) => x.idx);
    desiredOrder.forEach((fromIdxOriginal, targetPos) => {
      const currentPos = currentOrder.indexOf(fromIdxOriginal);
      if (currentPos !== targetPos) {
        move(currentPos, targetPos);
        const [moved] = currentOrder.splice(currentPos, 1);
        currentOrder.splice(targetPos, 0, moved);
      }
    });
  }, [watchedMonths, move]);

  useEffect(() => {
    if (clientMode !== 'existing') {
      setClients([]);
      setCurrentPage(1);
      return;
    }
    const q = (clientSearch || '').trim();
    const t = setTimeout(
      async () => {
        try {
          setIsSearchingClients(true);
          // Buscar clientes que já têm contratos com o prestador
          // Se busca vazia, retorna todos; se tem busca, filtra
          const res = await api.get<ClientData[]>('/provider/clients', {
            params: q.length >= 2 ? { search: q } : {},
          });
          setClients(res.data || []);
          setCurrentPage(1); // Reset para primeira página quando busca muda
        } catch {
          setClients([]);
          setCurrentPage(1);
        } finally {
          setIsSearchingClients(false);
        }
      },
      q.length >= 2 ? 350 : 200,
    );
    return () => clearTimeout(t);
  }, [clientMode, clientSearch]);

  const validateStep = () => {
    const v = getValues();
    if (currentStep === 1) {
      if (v.clientMode === 'existing') {
        if (!v.clientId) return 'Selecione um cliente.';
      } else {
        if (!v.clientName?.trim()) return 'Informe o nome do cliente.';
        if (!v.clientEmail?.trim()) return 'Informe o email do cliente.';
        if (!v.clientPhone?.trim()) return 'Informe o telefone do cliente.';
      }
    }
    if (currentStep === 2) {
      if (!v.serviceId) return 'Selecione um serviço.';
      if (!isValidUUID(v.serviceId)) return 'Selecione um serviço válido.';
      if (!v.price || String(v.price).trim() === '') return 'Informe um valor base.';
      const price = moneyToNumber(v.price);
      if (price === undefined || price <= 0)
        return 'Informe um valor base válido (maior que zero).';

      const startDateIso = brDateToIso(v.startDate);
      if (!startDateIso) {
        const startDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v.startDate || '');
        if (startDateMatch) {
          const year = Number(startDateMatch[3]);
          if (year < MIN_YEAR) {
            return `Data de início deve ser a partir de ${MIN_YEAR}.`;
          }
          if (year > MAX_YEAR) {
            return `Data de início deve ser até ${MAX_YEAR}.`;
          }
        }
        return `Informe uma data de início válida (DD/MM/AAAA). Deve estar entre ${MIN_YEAR} e ${MAX_YEAR}.`;
      }

      if (!v.continueCharging) {
        const endDateIso = brDateToIso(v.endDate); // Não permite futuro - até mês/ano atual
        if (!endDateIso) {
          const endDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v.endDate || '');
          if (endDateMatch) {
            const year = Number(endDateMatch[3]);
            if (year < MIN_YEAR) {
              return `Data de fim deve ser a partir de ${MIN_YEAR}.`;
            }
            const today = new Date();
            if (year > today.getFullYear()) {
              return `Data de fim não pode ser no futuro. Use até ${today.getFullYear()}.`;
            }
            // Se o ano é o atual, verificar se o mês não é futuro
            if (year === today.getFullYear()) {
              const month = Number(endDateMatch[2]);
              const currentMonth = today.getMonth() + 1;
              if (month > currentMonth) {
                return `Data de fim não pode ser no futuro. Use até ${String(currentMonth).padStart(2, '0')}/${today.getFullYear()}.`;
              }
            }
          }
          return `Informe uma data de fim válida (DD/MM/AAAA). Deve estar entre ${MIN_YEAR} e até o mês/ano atual.`;
        }
      }

      // Validações de agendamento (opcional)
      if (v.serviceSchedule?.frequency) {
        if (
          v.serviceSchedule.frequency === 'WEEKLY' &&
          (!v.serviceSchedule.daysOfWeek || v.serviceSchedule.daysOfWeek.length === 0)
        ) {
          return 'Selecione pelo menos um dia da semana para o agendamento.';
        }
        if (v.serviceSchedule.frequency === 'MONTHLY') {
          const dayOfMonth = Number(v.serviceSchedule.dayOfMonth);
          if (!dayOfMonth || dayOfMonth < 1 || dayOfMonth > 31) {
            return 'Dia do mês para agendamento deve ser entre 1 e 31.';
          }
        }
      }

      // Validações de cobrança (obrigatório se continueCharging = true)
      if (v.continueCharging) {
        if (!v.chargeSchedule?.billingModel) {
          return 'Selecione o modelo de cobrança.';
        }

        if (v.chargeSchedule.billingModel === 'RECURRING') {
          if (!v.chargeSchedule.recurrenceInterval) {
            return 'Selecione o intervalo da recorrência.';
          }

          if (v.chargeSchedule.recurrenceInterval === 'MONTHLY') {
            const day = Number(v.chargeSchedule.chargeDay);
            if (!day || day < 1 || day > 28) {
              return 'Dia de cobrança deve ser entre 1 e 28.';
            }
          } else if (v.chargeSchedule.recurrenceInterval === 'WEEKLY') {
            const day = Number(v.chargeSchedule.chargeDay);
            if (day === undefined || day < 0 || day > 6) {
              return 'Selecione um dia da semana válido para cobrança.';
            }
          }
        } else if (v.chargeSchedule.billingModel === 'ONE_TIME') {
          const dueDateIso = brDateToIso(v.chargeSchedule.dueDate, true); // Permite futuro
          if (!dueDateIso) {
            return 'Informe uma data de vencimento válida para cobrança única.';
          }
        }

        // Validação de dados de pagamento (pelo menos um deve ser preenchido)
        const hasPix = !!v.pixCopyPaste?.trim();
        const hasLink = !!v.paymentLink?.trim();
        const hasBank = !!(v.bankName?.trim() && v.bankAgency?.trim() && v.bankAccount?.trim());

        if (!hasPix && !hasLink && !hasBank) {
          return 'Informe pelo menos uma forma de pagamento (Pix, Link ou Dados Bancários) para continuar cobrando.';
        }
      }
    }
    if (currentStep === 3) {
      if (!v.months?.length) return 'Nenhum mês encontrado no período. Verifique as datas.';
      for (const m of v.months) {
        if (!m.competence) continue; // Pula se não tiver competência (não deve acontecer, mas é seguro)

        // Validar competência (YYYY-MM) está dentro dos limites
        const [yearStr] = m.competence.split('-');
        const year = Number(yearStr);
        if (year < MIN_YEAR || year > MAX_YEAR) {
          return `Mês ${m.competence} está fora do limite permitido (${MIN_YEAR} a ${MAX_YEAR}).`;
        }

        // Só valida se o mês foi marcado como recebido
        if (m.received) {
          // Se informou um valor, deve ser válido. Se não informou, usa o valor base automaticamente
          if (m.amount && m.amount.trim() !== '') {
            const amt = moneyToNumber(m.amount);
            if (amt === undefined || amt <= 0)
              return 'O valor informado para o mês deve ser válido (maior que zero).';
          }
          if (m.paidAt) {
            const paidAtIso = brDateToIso(m.paidAt);
            if (!paidAtIso) {
              const paidAtMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(m.paidAt || '');
              if (paidAtMatch) {
                const paidYear = Number(paidAtMatch[3]);
                if (paidYear < MIN_YEAR) {
                  return `Data de recebimento deve ser a partir de ${MIN_YEAR}.`;
                }
                if (paidYear > MAX_YEAR) {
                  return `Data de recebimento deve ser até ${MAX_YEAR}.`;
                }
              }
              return `Data de recebimento inválida. Deve estar entre ${MIN_YEAR} e ${MAX_YEAR}.`;
            }
          }
        }
      }
    }
    return null;
  };

  const handleNext = async () => {
    setError(null);
    const msg = validateStep();
    if (msg) {
      setError(msg);
      return;
    }

    // Se está no Step 1 e é "Criar novo", criar cliente antes de avançar
    if (currentStep === 1) {
      const v = getValues();
      if (v.clientMode === 'new' && !v.clientId) {
        setIsLoading(true);
        try {
          const res = await api.post<{ client: { id: string }; passwordSetupToken: string }>(
            '/client/register-by-provider',
            {
              username: v.clientName,
              email: v.clientEmail,
              phone: v.clientPhone,
              address: v.clientAddress,
            },
          );
          // Salva o clientId retornado no form
          const clientId = res.data?.client?.id;
          if (!clientId) {
            throw new Error('Cliente criado mas ID não retornado.');
          }
          setValue('clientId', clientId, { shouldDirty: true });
          showToast('Cliente criado e convite enviado!', 'success');
        } catch (err: any) {
          const message = getApiErrorMessage(err, 'Erro ao criar cliente. Verifique os dados.');
          setError(message);
          showToast(message, 'error');
          setIsLoading(false);
          return;
        } finally {
          setIsLoading(false);
        }
      }
    }

    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  };

  const handlePrevious = () => {
    setError(null);
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async () => {
    setError(null);
    const msg = validateStep();
    if (msg) {
      setError(msg);
      return;
    }

    const v = getValues();

    // Extra validation before submit
    if (!v.serviceId || !isValidUUID(v.serviceId)) {
      setError('Selecione um serviço válido.');
      return;
    }

    const basePrice = moneyToNumber(v.price);
    const payload: any = {
      serviceId: v.serviceId,
      price: basePrice,
      startDate: brDateToIso(v.startDate),
      continueCharging: v.continueCharging,
      endDate: v.continueCharging ? undefined : brDateToIso(v.endDate),

      // Agendamento do serviço (se fornecido)
      serviceSchedules: v.serviceSchedule?.frequency
        ? {
            frequency: v.serviceSchedule.frequency,
            daysOfWeek:
              v.serviceSchedule.frequency === 'WEEKLY'
                ? v.serviceSchedule.daysOfWeek?.map(Number)
                : undefined,
            dayOfMonth:
              v.serviceSchedule.frequency === 'MONTHLY'
                ? Number(v.serviceSchedule.dayOfMonth)
                : undefined,
            startTime: v.serviceSchedule.startTime,
            endTime: v.serviceSchedule.endTime,
          }
        : undefined,

      // Configuração de cobrança (refatorada)
      chargeSchedule: v.continueCharging
        ? {
            billingModel: v.chargeSchedule?.billingModel || 'RECURRING',
            recurrenceInterval:
              v.chargeSchedule?.billingModel === 'RECURRING'
                ? v.chargeSchedule?.recurrenceInterval
                : undefined,
            chargeDay: v.chargeSchedule?.chargeDay ? Number(v.chargeSchedule.chargeDay) : undefined,
            dueDate:
              v.chargeSchedule?.billingModel === 'ONE_TIME'
                ? brDateToIso(v.chargeSchedule.dueDate, true)
                : undefined,
            reminderType: v.chargeSchedule?.reminderType || 'THREE_DAYS_BEFORE_AND_ON_DUE',
            customReminderDays: v.chargeSchedule?.customReminderDays
              ? Number(v.chargeSchedule.customReminderDays)
              : undefined,
          }
        : undefined,

      // Dados de pagamento
      pixCopyPaste: v.pixCopyPaste,
      paymentLink: v.paymentLink,
      bankName: v.bankName,
      bankAgency: v.bankAgency,
      bankAccount: v.bankAccount,
      paymentInstructions: v.paymentInstructions,

      // Histórico de meses
      months: (v.months || []).map((m) => {
        const monthAmount = m.received ? moneyToNumber(m.amount) : undefined;
        return {
          competence: m.competence,
          received: m.received,
          // Se não informou valor ou valor inválido, usa o valor base
          amount: m.received ? (monthAmount !== undefined ? monthAmount : basePrice) : undefined,
          paidAt: m.received ? brDateToIso(m.paidAt) : undefined,
        };
      }),
    };

    // Se clientId já existe (cliente foi criado no Step 1), usa ele
    // Caso contrário, cria agora (fallback - não deveria acontecer)
    if (v.clientId && isValidUUID(v.clientId)) {
      payload.clientId = v.clientId;
    } else if (v.clientMode === 'new') {
      // Fallback: criar cliente se não foi criado antes
      payload.client = {
        username: v.clientName,
        email: v.clientEmail,
        phone: v.clientPhone,
        address: v.clientAddress,
      };
    } else {
      setError('Cliente não selecionado ou inválido.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await api.post('/provider/retroactive-onboarding/enrollment', payload);
      showToast('Contrato retroativo criado', 'success');
      const enrollmentId = res.data?.enrollmentId;
      if (enrollmentId) router.replace(`/(tabs)/(provider)/enrollments/${enrollmentId}` as any);
      else router.replace('/(tabs)/(provider)/enrollments' as any);
    } catch (err: any) {
      if (!showPlanLimit(err)) {
        const message = getApiErrorMessage(
          err,
          'Erro ao criar contrato retroativo. Verifique os dados.',
        );
        setError(message);
        showToast(message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <View className="min-h-[60px] w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
            <View className="mb-2 flex-row items-center justify-between">
              <ThemedText className="font-semibold text-base text-foreground">Cliente</ThemedText>
            </View>
            <ThemedText className="mb-2 text-sm text-muted-foreground">
              Selecione um cliente existente ou crie um novo
            </ThemedText>
            <View className="gap-4">
              <ControlledSelect
                control={control}
                name="clientMode"
                label="Como deseja cadastrar?"
                options={[
                  { label: 'Selecionar existente', value: 'existing' },
                  { label: 'Criar novo', value: 'new' },
                ]}
                disabled={!!getValues().clientId}
              />

              {clientMode === 'existing' || watch('clientId') ? (
                <>
                  {watch('clientId') && clientMode === 'new' && (
                    <View className="mb-2 flex-row items-center gap-2 rounded-xl border border-success-200 bg-success-50 px-4 py-3">
                      <Ionicons className="text-success-600" name="checkmark-circle" size={18} />
                      <ThemedText className="flex-1 font-medium text-sm text-success-700">
                        Cliente criado! Convite enviado por email e WhatsApp.
                      </ThemedText>
                    </View>
                  )}
                  <ControlledInput
                    control={control}
                    name="clientSearch"
                    label="Buscar cliente"
                    placeholder="Digite nome ou email..."
                    icon="search-outline"
                  />
                  {isSearchingClients && (
                    <ThemedText className="text-xs text-foreground/70">
                      Buscando clientes...
                    </ThemedText>
                  )}
                  {!isSearchingClients && clients.length === 0 && (
                    <ThemedText className="text-xs text-foreground/70">
                      {clientSearch
                        ? 'Nenhum cliente encontrado.'
                        : 'Você ainda não tem clientes com contratos. Use "Criar novo" para adicionar.'}
                    </ThemedText>
                  )}
                  {!isSearchingClients && clients.length > 0 && (
                    <View className="gap-3">
                      <ThemedText className="text-xs text-foreground/70">
                        {watch('clientId') ? 'Cliente selecionado:' : 'Selecione um cliente:'}
                      </ThemedText>
                      <View className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                        <ScrollView
                          keyboardShouldPersistTaps="handled"
                          horizontal
                          showsHorizontalScrollIndicator={true}
                        >
                          <View style={{ minWidth: '100%' }}>
                            <DataTable>
                              <DataTable.Header>
                                <DataTable.Title style={{ minWidth: 200 }}>
                                  <ThemedText className="font-semibold text-xs uppercase tracking-wide text-foreground/70">
                                    Nome
                                  </ThemedText>
                                </DataTable.Title>
                                <DataTable.Title style={{ minWidth: 250 }}>
                                  <ThemedText className="font-semibold text-xs uppercase tracking-wide text-foreground/70">
                                    Email
                                  </ThemedText>
                                </DataTable.Title>
                                <DataTable.Title style={{ minWidth: 140 }}>
                                  <ThemedText className="font-semibold text-xs uppercase tracking-wide text-foreground/70">
                                    Telefone
                                  </ThemedText>
                                </DataTable.Title>
                                <DataTable.Title style={{ minWidth: 100 }}>
                                  <ThemedText className="font-semibold text-xs uppercase tracking-wide text-foreground/70">
                                    Contratos
                                  </ThemedText>
                                </DataTable.Title>
                                <DataTable.Title style={{ minWidth: 100 }}>
                                  <ThemedText className="font-semibold text-xs uppercase tracking-wide text-foreground/70">
                                    Selecionar
                                  </ThemedText>
                                </DataTable.Title>
                              </DataTable.Header>

                              {paginatedClients.map((client) => {
                                const isSelected = watch('clientId') === client.id;
                                return (
                                  <Pressable
                                    key={client.id}
                                    onPress={() => {
                                      setValue('clientId', client.id, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      });
                                    }}
                                  >
                                    <DataTable.Row
                                      style={{
                                        backgroundColor: isSelected
                                          ? 'rgba(var(--primary-rgb, 59, 130, 246), 0.1)'
                                          : 'transparent',
                                      }}
                                    >
                                      <DataTable.Cell style={{ minWidth: 200 }}>
                                        <ThemedText
                                          className={
                                            isSelected
                                              ? 'font-semibold text-primary'
                                              : 'font-medium text-foreground'
                                          }
                                        >
                                          {client.user?.username || client.user?.email || 'Cliente'}
                                        </ThemedText>
                                      </DataTable.Cell>
                                      <DataTable.Cell style={{ minWidth: 250 }}>
                                        <ThemedText className="text-sm text-foreground/70">
                                          {client.user?.email || '-'}
                                        </ThemedText>
                                      </DataTable.Cell>
                                      <DataTable.Cell style={{ minWidth: 140 }}>
                                        <ThemedText className="text-sm text-foreground/70">
                                          {client.phone || '-'}
                                        </ThemedText>
                                      </DataTable.Cell>
                                      <DataTable.Cell style={{ minWidth: 100 }}>
                                        <ThemedText className="text-sm text-foreground/70">
                                          {client.enrollments?.length || 0} contrato
                                          {(client.enrollments?.length || 0) !== 1 ? 's' : ''}
                                        </ThemedText>
                                      </DataTable.Cell>
                                      <DataTable.Cell style={{ minWidth: 100 }}>
                                        <View className="items-center">
                                          {isSelected ? (
                                            <View className="h-6 w-6 items-center justify-center rounded-full bg-primary">
                                              <Ionicons name="checkmark" size={16} color="white" />
                                            </View>
                                          ) : (
                                            <View className="h-6 w-6 items-center justify-center rounded-full border-2 border-border" />
                                          )}
                                        </View>
                                      </DataTable.Cell>
                                    </DataTable.Row>
                                  </Pressable>
                                );
                              })}
                            </DataTable>
                          </View>
                        </ScrollView>
                      </View>
                      {totalPages > 1 && (
                        <View className="mt-3">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                          />
                          <ThemedText className="mt-2 text-center text-xs text-foreground/70">
                            Mostrando {(currentPage - 1) * itemsPerPage + 1} -{' '}
                            {Math.min(currentPage * itemsPerPage, clients.length)} de{' '}
                            {clients.length} clientes
                          </ThemedText>
                        </View>
                      )}
                    </View>
                  )}
                </>
              ) : (
                <>
                  <ControlledInput
                    control={control}
                    name="clientName"
                    label="Nome"
                    placeholder="Maria Silva"
                    icon="person-outline"
                  />
                  <ControlledInput
                    control={control}
                    name="clientEmail"
                    label="Email"
                    placeholder="maria@email.com"
                    type="email"
                    icon="mail-outline"
                  />
                  <ControlledInput
                    control={control}
                    name="clientPhone"
                    label="Telefone"
                    placeholder="(11) 90000-0000"
                    maskType="phone"
                    icon="call-outline"
                  />
                  <ControlledInput
                    control={control}
                    name="clientAddress"
                    label="Endereço (opcional)"
                    placeholder="Rua..."
                    icon="location-outline"
                  />
                </>
              )}
            </View>
          </View>
        );

      case 2:
        return (
          <>
            <View className="min-h-[60px] w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
              <View className="mb-2 flex-row items-center justify-between">
                <ThemedText className="font-semibold text-base text-foreground">
                  Contrato
                </ThemedText>
              </View>
              <ThemedText className="mb-2 text-sm text-muted-foreground">
                Configure o contrato retroativo
              </ThemedText>
              <View className="gap-4">
                <ControlledDataTableSelect
                  control={control}
                  name="serviceId"
                  label="Serviço"
                  modalTitle="Selecione um Serviço"
                  fetchEndpoint="provider/services"
                  valueAccessor={(item: any) => item.id}
                  displayAccessor={(item: any) => item.name}
                  onSelectItem={(item: any) => {
                    if (item?.defaultPrice) {
                      setValue('price', formatCurrency(item.defaultPrice));
                    }
                  }}
                  columns={[
                    { header: 'Serviço', accessor: 'name', flex: 2 },
                    {
                      header: 'Valor Base',
                      accessor: (item: any) => (
                        <ThemedText className="text-sm">
                          {item.defaultPrice ? formatCurrency(item.defaultPrice) : '-'}
                        </ThemedText>
                      ),
                      width: 120,
                      flex: 1,
                    },
                  ]}
                />
                <ControlledInput
                  control={control}
                  name="price"
                  label="Valor base (R$)"
                  placeholder="150,00"
                  maskType="currency"
                  icon="cash-outline"
                />
                <ControlledInput
                  control={control}
                  name="startDate"
                  label="Início (DD/MM/AAAA)"
                  placeholder="01/01/2020"
                  maskType="date"
                  icon="calendar-outline"
                />

                <Pressable
                  onPress={() =>
                    setValue('continueCharging', !continueCharging, { shouldDirty: true })
                  }
                  className="flex-row items-start gap-3 rounded-xl border border-border bg-card px-4 py-4"
                >
                  <View
                    className={`h-6 w-6 items-center justify-center rounded-md border ${
                      continueCharging
                        ? 'border-primary bg-primary'
                        : 'border-border bg-transparent'
                    }`}
                  >
                    {continueCharging && <Ionicons name="checkmark" size={16} color="white" />}
                  </View>
                  <View className="flex-1">
                    <ThemedText className="font-medium text-sm">
                      Vai continuar cobrando por aqui?
                    </ThemedText>
                    <ThemedText className="mt-1 text-xs text-foreground/70">
                      Se sim, vamos gerar cobranças só do mês atual para frente.
                    </ThemedText>
                  </View>
                </Pressable>

                {!continueCharging && (
                  <ControlledInput
                    control={control}
                    name="endDate"
                    label={`Fim (obrigatório) - Até mês/ano atual`}
                    placeholder="31/12/2025"
                    maskType="date"
                    icon="calendar-outline"
                  />
                )}
              </View>
            </View>

            {/* Card de Agendamento do Serviço */}
            <View className="min-h-[60px] w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
              <View className="mb-2 flex-row items-center justify-between">
                <ThemedText className="font-semibold text-base text-foreground">
                  Agendamento do Serviço
                </ThemedText>
              </View>
              <ThemedText className="mb-2 text-sm text-muted-foreground">
                Configure quando o serviço será realizado (opcional se não recorrente)
              </ThemedText>

              <View className="gap-4">
                <ControlledSelect
                  control={control}
                  name="serviceSchedule.frequency"
                  label="Frequência do Serviço"
                  options={frequencyOptions}
                  placeholder="Nenhuma (Data Única)"
                />

                {serviceFrequency === 'WEEKLY' && (
                  <ControlledSelect
                    control={control}
                    name="serviceSchedule.daysOfWeek"
                    label="Dias da Semana"
                    options={dayOfWeekOptions}
                    placeholder="Selecione os dias"
                    isMultiple={true}
                  />
                )}

                {serviceFrequency === 'MONTHLY' && (
                  <ControlledInput
                    control={control}
                    name="serviceSchedule.dayOfMonth"
                    label="Dia do Mês (1-31)"
                    placeholder="15"
                    type="number"
                    icon="calendar-outline"
                  />
                )}

                <View className="flex-row gap-2">
                  <View className="flex-1">
                    <ControlledInput
                      control={control}
                      name="serviceSchedule.startTime"
                      label="Horário de Início"
                      placeholder="09:00"
                      icon="time-outline"
                    />
                  </View>
                  <View className="flex-1">
                    <ControlledInput
                      control={control}
                      name="serviceSchedule.endTime"
                      label="Horário de Término (opcional)"
                      placeholder="10:00"
                      icon="time-outline"
                    />
                  </View>
                </View>
              </View>
            </View>

            {/* Card de Configuração de Cobrança */}
            {continueCharging && (
              <View className="min-h-[60px] w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
                <View className="mb-2 flex-row items-center justify-between">
                  <ThemedText className="font-semibold text-base text-foreground">
                    Configuração de Cobrança
                  </ThemedText>
                </View>
                <ThemedText className="mb-2 text-sm text-muted-foreground">
                  Configure como serão geradas as cobranças futuras
                </ThemedText>

                <View className="gap-4">
                  <ControlledSelect
                    control={control}
                    name="chargeSchedule.billingModel"
                    label="Modelo de Cobrança"
                    options={billingModelOptions}
                    placeholder="Selecione o modelo"
                  />

                  {billingModel === 'RECURRING' && (
                    <ControlledSelect
                      control={control}
                      name="chargeSchedule.recurrenceInterval"
                      label="Intervalo da Recorrência"
                      options={recurrenceIntervalOptions}
                      placeholder="Selecione o intervalo"
                    />
                  )}

                  {billingModel === 'RECURRING' && recurrenceInterval === 'MONTHLY' && (
                    <ControlledInput
                      control={control}
                      name="chargeSchedule.chargeDay"
                      label="Dia do Mês para Cobrança (1-28)"
                      placeholder="5"
                      type="number"
                      icon="calendar-outline"
                    />
                  )}

                  {billingModel === 'RECURRING' && recurrenceInterval === 'WEEKLY' && (
                    <ControlledSelect
                      control={control}
                      name="chargeSchedule.chargeDay"
                      label="Dia da Semana para Cobrança"
                      options={dayOfWeekOptions}
                      placeholder="Selecione o dia"
                    />
                  )}

                  {billingModel === 'ONE_TIME' && (
                    <ControlledInput
                      control={control}
                      name="chargeSchedule.dueDate"
                      label="Data de Vencimento"
                      placeholder="DD/MM/AAAA"
                      maskType="date"
                      icon="calendar-outline"
                    />
                  )}

                  {/* Lembretes */}
                  <View className="border-t border-border pt-4">
                    <ThemedText className="mb-3 font-semibold text-sm text-foreground">
                      Lembretes de Cobrança
                    </ThemedText>

                    <ControlledSelect
                      control={control}
                      name="chargeSchedule.reminderType"
                      label="Quando enviar lembretes"
                      options={reminderTypeOptions}
                    />

                    {reminderType === 'CUSTOM_DAYS_BEFORE_AND_ON_DUE' && (
                      <ControlledInput
                        control={control}
                        name="chargeSchedule.customReminderDays"
                        label="Quantos dias antes do vencimento?"
                        placeholder="Ex: 7"
                        type="number"
                      />
                    )}
                  </View>

                  {/* Dados de Pagamento */}
                  <View className="border-t border-border pt-4">
                    <ThemedText className="mb-3 font-semibold text-sm text-foreground">
                      Dados para Pagamento
                    </ThemedText>
                    <ThemedText className="mb-3 text-xs text-muted-foreground">
                      Informe os dados que o cliente usará para pagar
                    </ThemedText>

                    <View className="gap-3">
                      <ControlledInput
                        control={control}
                        name="pixCopyPaste"
                        label="Código Pix (Copia e Cola)"
                        placeholder="Cole aqui seu código Pix"
                        type="textarea"
                        icon="card-outline"
                      />

                      <ControlledInput
                        control={control}
                        name="paymentLink"
                        label="Link de Pagamento"
                        placeholder="https://..."
                        icon="link-outline"
                      />

                      <View className="border-t border-border pt-3">
                        <ThemedText className="mb-2 font-medium text-xs text-foreground">
                          Dados Bancários
                        </ThemedText>

                        <ControlledInput
                          control={control}
                          name="bankName"
                          label="Nome do Banco"
                          placeholder="Ex: Nubank, Itaú"
                          icon="business-outline"
                        />

                        <View className="flex-row gap-2">
                          <View className="flex-1">
                            <ControlledInput
                              control={control}
                              name="bankAgency"
                              label="Agência"
                              placeholder="0001"
                              type="number"
                            />
                          </View>
                          <View className="flex-[2]">
                            <ControlledInput
                              control={control}
                              name="bankAccount"
                              label="Conta"
                              placeholder="12345-6"
                            />
                          </View>
                        </View>
                      </View>

                      <ControlledInput
                        control={control}
                        name="paymentInstructions"
                        label="Instruções de Pagamento"
                        placeholder="Instruções adicionais..."
                        type="textarea"
                      />
                    </View>
                  </View>
                </View>
              </View>
            )}
          </>
        );

      case 3:
        return (
          <View className="min-h-[60px] w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
            <View className="mb-2 flex-row items-center justify-between">
              <ThemedText className="font-semibold text-base text-foreground">
                Meses passados
              </ThemedText>
            </View>
            <ThemedText className="mb-2 text-sm text-muted-foreground">
              Informe o histórico de recebimento por mês. Marque apenas os meses que foram
              recebidos.
            </ThemedText>
            <View className="gap-3">
              <View className="rounded-xl border border-border bg-canvas/30">
                <View className="flex-row items-center justify-between border-b border-border px-4 py-3">
                  <ThemedText className="font-semibold">Lista de meses</ThemedText>
                  <ThemedText className="text-xs text-foreground/70">
                    Mês 1 = mais antigo
                  </ThemedText>
                </View>

                <View className="gap-3 p-4">
                  {fields.map((f, idx) => {
                    const received = watch(`months.${idx}.received`);
                    return (
                      <View key={f.id} className="rounded-xl border border-border bg-canvas/30 p-3">
                        <View className="mb-2 flex-row items-center justify-between">
                          <ThemedText className="font-semibold">Mês {idx + 1}</ThemedText>
                        </View>

                        <View className="gap-2">
                          <ControlledSelect
                            control={control}
                            name={`months.${idx}.competence`}
                            label="Mês"
                            options={filteredMonthOpts}
                            disabled={true}
                            noOpacityWhenDisabled={true}
                          />

                          <Pressable
                            onPress={() =>
                              update(idx, {
                                ...getValues().months[idx],
                                received: !received,
                              })
                            }
                            className="flex-row items-center justify-between rounded-xl border border-border bg-card px-4 py-3"
                          >
                            <View className="flex-row items-center gap-3">
                              <View
                                className={`h-6 w-6 items-center justify-center rounded-md border ${
                                  received
                                    ? 'border-primary bg-primary'
                                    : 'border-border bg-transparent'
                                }`}
                              >
                                {received && <Ionicons name="checkmark" size={16} color="white" />}
                              </View>
                              <ThemedText className="font-medium text-sm">Recebeu</ThemedText>
                            </View>
                            <ThemedText className="text-xs text-foreground/70">
                              {received ? 'Pago' : 'Cancelado'}
                            </ThemedText>
                          </Pressable>

                          {received && (
                            <View className="gap-2">
                              <View className="flex-row gap-2">
                                <View className="flex-1">
                                  <ControlledInput
                                    control={control}
                                    name={`months.${idx}.amount`}
                                    label="Valor (R$) - Opcional"
                                    placeholder={getValues().price || '150,00'}
                                    maskType="currency"
                                    icon="cash-outline"
                                  />
                                  <ThemedText className="-mt-2 mb-2 text-xs text-foreground/70">
                                    Se não informar, será usado o valor base do contrato
                                  </ThemedText>
                                </View>
                                <View className="flex-1">
                                  <ControlledInput
                                    control={control}
                                    name={`months.${idx}.paidAt`}
                                    label="Pago em (opcional)"
                                    placeholder="31/01/2020"
                                    maskType="date"
                                    icon="calendar-outline"
                                  />
                                </View>
                              </View>
                            </View>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            </View>
          </View>
        );

      case 4: {
        const v = getValues();
        const total = (v.months || []).reduce((acc, m) => acc + (moneyToNumber(m.amount) || 0), 0);
        return (
          <View className="min-h-[60px] w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
            <View className="mb-2 flex-row items-center justify-between">
              <ThemedText className="font-semibold text-base text-foreground">Revisão</ThemedText>
            </View>
            <ThemedText className="mb-2 text-sm text-muted-foreground">
              Confira antes de finalizar
            </ThemedText>
            <View className="gap-2">
              <ThemedText className="text-sm text-foreground/70">
                Cliente: {v.clientMode === 'existing' ? v.clientId || '-' : v.clientName || '-'}
              </ThemedText>
              <ThemedText className="text-sm text-foreground/70">
                Serviço: {v.serviceId || '-'}
              </ThemedText>
              <ThemedText className="text-sm text-foreground/70">
                Meses: {v.months?.length || 0}
              </ThemedText>
              <ThemedText className="text-sm text-foreground/70">
                Total recebido (informado): R$ {total.toFixed(2)}
              </ThemedText>

              <View className="mt-3 flex-row items-center gap-2 rounded-xl border border-warning-200 bg-warning-50 px-4 py-3">
                <Ionicons className="text-warning-600" name="alert-circle" size={18} />
                <ThemedText className="flex-1 font-medium text-sm text-warning-700">
                  Atenção: este fluxo não cria cobranças pendentes no passado automaticamente.
                </ThemedText>
              </View>
            </View>
          </View>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <PageLayout
        title="Cadastro retroativo"
        subtitle="Cadastre contratos e histórico de pagamentos de meses anteriores"
      >
        {/* Progress Indicator */}
        <View className="w-full gap-3 rounded-xl bg-card p-4 shadow-sm">
          <View className="mb-2">
            <Progress value={progress} className="h-2" />
          </View>
          <View className="flex-row justify-between">
            {STEPS.map((step) => (
              <View key={step.id} className="flex-1 items-center">
                <View
                  className={`mb-1 h-8 w-8 items-center justify-center rounded-full ${
                    step.id <= currentStep ? 'bg-primary' : 'bg-gray-300'
                  }`}
                >
                  <Ionicons
                    name={step.icon as any}
                    size={16}
                    color={step.id <= currentStep ? 'white' : th('#9CA3AF')}
                  />
                </View>
                <ThemedText
                  className={`text-center text-xs ${
                    step.id <= currentStep ? 'text-primary' : 'text-gray-500'
                  }`}
                >
                  {step.title}
                </ThemedText>
              </View>
            ))}
          </View>
        </View>

        {/* Step Content */}
        {renderStep()}

        {/* Error Message */}
        {error && (
          <View className="flex-row items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3">
            <Ionicons className="text-error-600" name="alert-circle" size={18} />
            <ThemedText className="flex-1 font-medium text-sm text-error-600">{error}</ThemedText>
          </View>
        )}

        {/* Navigation Buttons */}
        <View className="flex-row gap-3">
          {currentStep > 1 && (
            <Button
              title="Voltar"
              onPress={handlePrevious}
              variant="outline"
              size="md"
              className="flex-1"
              disabled={isLoading}
            />
          )}

          {currentStep < STEPS.length ? (
            <Button
              title="Próximo"
              onPress={handleNext}
              size="md"
              className="flex-1"
              disabled={isLoading}
            />
          ) : (
            <Button
              title={isLoading ? 'Salvando...' : 'Finalizar'}
              onPress={handleSubmit}
              size="md"
              className="flex-1"
              disabled={isLoading}
            />
          )}
        </View>
      </PageLayout>
    </>
  );
}
